import { useTranslation } from 'react-i18next';
import {
  BadgeTitle,
  BadgeValue,
  CardBody,
  InfoBadge,
  NoDataContainer,
  RightContent,
} from '../styles';
import Card from '@/components/Card';
import Typography from '@mui/material/Typography';
import DescriptionPopper from '@/components/DescriptionPopper';
import { DescriptionComponent } from '../Tree/styles';
import { useEffect, useMemo, useState } from 'react';
import { EditButton } from '../General/styles';
import { Pencil, UserPen } from 'lucide-react';
import { MainHeader } from '../Wireless/styles';
import { managementFields, webUserFields } from '@/utils/constants';
import ModalEditor from '@/components/ModalEditor';
import { useEquipmentContext } from '@/context/EquipmentContext';
import api from '@/services/api';
import { enqueueSnackbar } from 'notistack';
import Skeleton from '@mui/material/Skeleton';
import SelectAp from '../Wireless/SelectAp';
import axios from 'axios';
import { useTheme } from '@mui/material';

function Management({ modeView }: { modeView: string }) {
  const { t } = useTranslation();
  const theme = useTheme();

  const { equipment, refetch, loading } = useEquipmentContext();

  const [currentUser, setCurrentUser] = useState(0);
  const [singleUser, setSingleUser] = useState<boolean>(true);

  const [currentModal, setCurrentModal] = useState<
    'management' | 'selectWebUser' | 'webUser' | null
  >(null);

  const managementExternalOptions = useMemo(() => {
    const manage = equipment?.management;

    if (!manage) return {};

    const options: Record<string, { options: string[] }> = {};
    managementFields.forEach((field) => {
      const fieldData = manage[field.field as keyof ManagementType];
      if (fieldData?.options && Array.isArray(fieldData.options)) {
        options[field.field] = { options: fieldData.options };
      }
    });
    return options;
  }, [equipment]);

  useEffect(() => {
    setSingleUser(equipment?.webUsers ? equipment.webUsers.length < 2 : true);
  }, [equipment]);

  // If enable is null, still allow editing user info
  const currentWebUsersData = useMemo(() => {
    if (!equipment?.webUsers?.[currentUser]) return null;

    const user = { ...equipment.webUsers[currentUser] } as WebUserType;
    if (user.enable === null) {
      user.enable = { id: -1, value: 'true', writable: 0 };
    }
    return user;
  }, [equipment, currentUser]);

  // Update on router and database the management parameters
  const handleEdit = async (
    interfaceType: 'management' | 'selectWebUser' | 'webUser',
    fields: { field: keyof ManagementType | keyof WebUserType; type: string }[],
    vals: Record<string, string | number>,
    userIndex?: number
  ) => {
    if (!equipment || !equipment.management) return;

    const interfaceData: ManagementType | WebUserType | undefined =
      interfaceType === 'webUser' && userIndex !== undefined
        ? equipment?.webUsers?.[userIndex]
        : equipment?.management;

    const changedFields = fields
      .map((field) => {
        const fieldValue =
          interfaceData?.[field.field as keyof typeof interfaceData];

        // Skip non-ParameterValueType values (e.g. enable: boolean)
        if (
          !fieldValue ||
          typeof fieldValue !== 'object' ||
          !('id' in fieldValue)
        )
          return null;

        const param = fieldValue as ParameterValueType;
        const newValue = vals[field.field as string] ?? '';

        if (param.id && String(param.value ?? '') !== String(newValue ?? ''))
          return { id: param.id, value: String(newValue) };

        return null;
      })
      .filter(Boolean) as { id: number; value: string }[];

    if (changedFields.length === 0) {
      setCurrentModal(null);
      return;
    }

    console.log(changedFields);

    try {
      const response = await api.patch(
        `/devices/${equipment.id}/set-parameters`,
        changedFields
      );

      if (response.status === 200) {
        refetch();
        enqueueSnackbar(t('equipment.webUser.editSuccess'), {
          variant: 'success',
        });
        setCurrentModal(null);
      } else if (response.status === 400) {
        enqueueSnackbar(t('equipment.webUser.editForbidden'), {
          variant: 'error',
        });
        setCurrentModal(null);
      }
    } catch (error) {
      // if response 400, means router doesn't allow edit this parameter
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        enqueueSnackbar(t('equipment.webUser.editForbidden'), {
          variant: 'error',
        });
        setCurrentModal(null);
      } else {
        // generic error message
        enqueueSnackbar(t('config.model.editFailed'), { variant: 'error' });
      }
    }
  };

  const handleEditManagement = (vals: Record<string, string | number>) =>
    handleEdit('management', managementFields, vals);
  const handleEditWebUser = (vals: Record<string, string | number>) =>
    handleEdit('webUser', webUserFields, vals, currentUser);

  return (
    <RightContent>
      {/** Management server data */}
      <Card padding="24px">
        <MainHeader>
          <div className="divHeader">
            <Typography variant="h3">
              {t('equipment.management.tab')}
            </Typography>
          </div>
          {equipment?.management && (
            <div className="divHeader">
              <DescriptionPopper
                description={
                  <DescriptionComponent>
                    {t('equipment.management.edit')}
                  </DescriptionComponent>
                }
                placement="top"
                children={
                  <EditButton onClick={() => setCurrentModal('management')}>
                    <Pencil size={18} />
                  </EditButton>
                }
              />
            </div>
          )}
        </MainHeader>

        {equipment?.management ? (
          <CardBody modeView={modeView === 'grid'}>
            {managementFields.map((field) => {
              const current =
                equipment?.management?.[field.field as keyof ManagementType]
                  ?.value;
              return (
                <InfoBadge key={field.field} modeView={modeView}>
                  <BadgeTitle>
                    {t(`equipment.management.${field.field}`)}
                  </BadgeTitle>
                  {!loading ? (
                    <BadgeValue empty={current === ''}>
                      {current === '' ? t('equipment.noValue') : current}
                    </BadgeValue>
                  ) : (
                    <Skeleton
                      variant="rounded"
                      width={modeView === 'grid' ? '225px' : '100%'}
                      height={14}
                    />
                  )}
                </InfoBadge>
              );
            })}
          </CardBody>
        ) : (
          <NoDataContainer>
            <Typography variant="h6" color={theme.palette.grey['01']}>
              {t('equipment.management.noManagementData')}
            </Typography>
          </NoDataContainer>
        )}
      </Card>

      {/** Web user data */}
      <Card padding="24px">
        <MainHeader>
          <div className="divHeader">
            <Typography variant="h3">{t('equipment.webUser.title')}</Typography>
          </div>
          <div className="divHeader">
            {!singleUser && (
              <DescriptionPopper
                description={
                  <DescriptionComponent>
                    {t('equipment.webUser.selectWebUser')}
                  </DescriptionComponent>
                }
                placement="top"
                children={
                  <EditButton onClick={() => setCurrentModal('selectWebUser')}>
                    <UserPen size={18} />
                  </EditButton>
                }
              />
            )}

            {equipment?.webUsers?.[currentUser] && (
              <DescriptionPopper
                description={
                  <DescriptionComponent>
                    {t('equipment.webUser.edit')}
                  </DescriptionComponent>
                }
                placement="top"
                children={
                  <EditButton onClick={() => setCurrentModal('webUser')}>
                    <Pencil size={18} />
                  </EditButton>
                }
              />
            )}
          </div>
        </MainHeader>

        {equipment?.webUsers?.[currentUser] ? (
          <CardBody modeView={modeView === 'grid'}>
            {webUserFields.map((field) => {
              const user = equipment?.webUsers?.[currentUser];

              const current = user?.[field.field as keyof WebUserType]?.value;

              return (
                <InfoBadge key={field.field} modeView={modeView}>
                  <BadgeTitle>
                    {t(`equipment.webUser.${field.field}`)}
                  </BadgeTitle>
                  {!loading ? (
                    <BadgeValue empty={current === ''}>
                      {current === '' ? t('equipment.noValue') : current}
                    </BadgeValue>
                  ) : (
                    <Skeleton
                      variant="rounded"
                      width={modeView === 'grid' ? '225px' : '100%'}
                      height={14}
                    />
                  )}
                </InfoBadge>
              );
            })}
          </CardBody>
        ) : (
          <NoDataContainer>
            <Typography variant="h6" color={theme.palette.grey['01']}>
              {t('equipment.webUser.noWebUserData')}
            </Typography>
          </NoDataContainer>
        )}
      </Card>

      <ModalEditor
        title={t('equipment.management.edit')}
        open={currentModal === 'management'}
        setOpen={setCurrentModal}
        fieldList={managementFields}
        handleSubmit={handleEditManagement}
        fieldValues={equipment?.management ?? undefined}
        icon="cog"
        translatePath="equipment.management"
        externalOptions={managementExternalOptions}
      />

      <SelectAp
        title={t('equipment.webUser.changeWebUser')}
        label={t('equipment.webUser.selectWebUser')}
        open={currentModal === 'selectWebUser'}
        setOpen={setCurrentModal}
        currentValue={currentUser}
        setCurrentValue={setCurrentUser}
        currentOptions={
          equipment?.webUsers?.map((user, index) => ({
            index,
            name: String(user.alias?.value ?? `Web User ${index + 1}`),
          })) || []
        }
      />

      <ModalEditor
        title={t('equipment.webUser.edit')}
        open={currentModal === 'webUser'}
        setOpen={setCurrentModal}
        fieldList={webUserFields}
        handleSubmit={handleEditWebUser}
        fieldValues={currentWebUsersData ?? undefined}
        icon="user-cog"
        translatePath="equipment.webUser"
        externalOptions={managementExternalOptions}
      />
    </RightContent>
  );
}

export default Management;
